import { CodeQueryParams, CodeResponse, CreateCodeRequest, UpdateCodeRequest, DeleteCodeRequest } from "@/interface";
import { api } from "@/lib/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const useCodeList = (params: CodeQueryParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["codeList", params],
    queryFn: () => api.get("/code", { params }),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
    enabled: options?.enabled ?? true,

    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useCodeNameSearch = (name: string) => {
  return useMutation({
    mutationKey: ["codeNameSearch", name],
    mutationFn: (name: string) => api.get(`/code/search/${name}`),
    onSuccess: res => {
      if (res.code === 200) {
        // toast.success("코드 검색 완료");
        // console.log(res.data);
        return res.data;
      } else {
        toast.error(res.message);
      }
    },
    onError: err => {
      toast.error("코드 검색 실패" + err);
    },
  });
};

export const useCodeListByCompanyCode = () => {
  return useQuery({
    queryKey: ["codeListByCompanyCode"],
    queryFn: () => api.get(`/code/company`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeListByItemGroupCode = () => {
  return useQuery({
    queryKey: ["codeListByItemGroupCode"],
    queryFn: () => api.get(`/code/item-group`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
    }
    return res.data;
    },
  });
};

export const useCodeListByDutyCode = () => {
  return useQuery({
    queryKey: ["codeListByDutyCode"],
    queryFn: () => api.get(`/code/duty-code`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeListByTeamCode = () => {
  return useQuery({
    queryKey: ["codeListByTeamCode"],
    queryFn: () => api.get(`/code/team-code`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeListByVehicleGb1 = () => {

  return useQuery({
    queryKey: ["codeListByVehicleGb1"],
    queryFn: () => api.get(`/code/vehicle-gb1`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeListByVehicleGb2 = () => {
  return useQuery({
    queryKey: ["codeListByVehicleGb2"],
    queryFn: () => api.get(`/code/vehicle-gb2`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeListByCargoForm = () => {
  return useQuery({
    queryKey: ["codeListByCargoForm"],
    queryFn: () => api.get(`/code/cargo-form`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeListByCargoUnit = () => {
  return useQuery({
    queryKey: ["codeListByCargoUnit"],
    queryFn: () => api.get(`/code/cargo-unit`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeListByPriceCode = () => {
  return useQuery({
    queryKey: ["codeListByPriceCode"],
    queryFn: () => api.get(`/code/price-code`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      } 
      return res.data;
    },
  });
};

export const useCodeListByPriceExtraCode = () => {
  return useQuery({
    queryKey: ["codeListByPriceExtraCode"],
    queryFn: () => api.get(`/code/price-extra-code`),
    select: (res: CodeResponse) => {
      if (res.code !== 200) {
        toast.error(res.message);
        return [];
      }
      return res.data;
    },
  });
};

export const useCodeInsert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["codeInsert"],
    mutationFn: (data: CreateCodeRequest) => api.post("/code", data),
    onSuccess: res => {
      if (res.code === 200) {
        toast.success("공통 코드 추가 완료");
        // 모든 codeList 쿼리 초기화
        queryClient.invalidateQueries({ queryKey: ["codeList"] });
      } else {
        toast.error(res.message);
      }
    },
    onError: err => {
      toast.error("공통 코드 추가 실패" + err);
    },
  });
};

export const useCodeUpdate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateCodeRequest) => api.put<CodeResponse>("/code", dto),
    onSuccess: (res, dto) => {
      if (res.code === 200) {
        toast.success("공통 코드 수정 완료");
        qc.setQueriesData({ queryKey: ["codeList"], exact: false }, (old: any) => {
          if (!old) return old;

          const list = Array.isArray(old) ? old : old.data;
          if (!Array.isArray(list)) return old;

          const next = list.map((it: any) => (it.codeId === dto.codeId ? { ...it, ...dto } : it));

          return Array.isArray(old) ? next : { ...old, data: next };
        });

        // 안전하게 서버 재요청
        qc.invalidateQueries({ queryKey: ["codeList"], exact: false });
      } else {
        toast.error(res.message);
      }
    },
    onError: err => {
      console.log(err);
    },
  });
};

export const useCodeDelete = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (codeIds: number[]) => api.delete("/code", { data: codeIds }),

    onSuccess: (_res, codeIds) => {
      toast.success("공통 코드 삭제 완료");

      // 모든 codeList 캐시에서 삭제된 항목 제거(optimistic)
      qc.setQueriesData({ queryKey: ["codeList"], exact: false }, (old: any) => {
        if (!old) return old;

        const list = Array.isArray(old) ? old : old.data;
        if (!Array.isArray(list)) return old;

        const next = list.filter((it: any) => !codeIds.includes(it.codeId));
        return Array.isArray(old) ? next : { ...old, data: next };
      });

      // 안전하게 서버 재동기화
      qc.invalidateQueries({ queryKey: ["codeList"], exact: false });
    },
    onError: err => {
      console.log(err);
    },
  });
};
